import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';
import useSEOMeta from '@/hooks/useSEOMeta';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const category = article ? categories.find((c) => c.id === article.categoryId) : undefined;
  const relatedArticles = article ? getRelatedArticles(article, 3) : [];

  useSEOMeta({
    title: article ? `${article.title} | 苍井空人物志` : '文章未找到 | 苍井空人物志',
    description: article ? article.excerpt : '文章未找到',
    keywords: article ? `苍井空,${article.tags.join(',')},苍井空个人资料` : '苍井空',
    canonicalUrl: article ? `https://cangjingkong.cn/article/${article.slug}` : 'https://cangjingkong.cn/',
    ogType: 'article',
    ogImage: article?.coverImage,
    ogTitle: article?.title,
    ogDescription: article?.excerpt,
  });

  if (!article) {
    return <NotFound />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '苍井空人物志',
      url: 'https://cangjingkong.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://cangjingkong.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                ...(category ? [{ name: category.name, path: `/category/${category.slug}` }] : []),
                { name: article.title },
              ]}
            />
          </div>

          <article className="pb-16">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>

                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-block mt-3 px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
              </div>

              <div className="mb-10 rounded-lg overflow-hidden aspect-[16/9]">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.contentHtml }}
              />

              <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200/70">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-20 bg-background-100/70">
              <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}